import React, { useState, useEffect } from 'react';

const NewConversationModal = ({ onClose, onCreateConversation, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [conversationTitle, setConversationTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const searchUsers = async () => {
    try {
      setSearchLoading(true);
      const response = await fetch(`/api/v1/search?q=${encodeURIComponent(searchTerm)}&type=user&per_page=10`);
      const data = await response.json();

      if (data.results) {
        // Filter out current user and already selected users
        const filteredUsers = data.results.filter(user =>
          user.username !== currentUser.username &&
          !selectedUsers.some(selected => selected.username === user.username)
        );
        setSearchResults(filteredUsers);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUsers(prev => [...prev, user]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleUserRemove = (userToRemove) => {
    setSelectedUsers(prev => prev.filter(user => user.username !== userToRemove.username));
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;

    setLoading(true);

    try {
      const usernames = selectedUsers.map(user => user.username);
      const title = selectedUsers.length > 1 ? conversationTitle : '';

      await onCreateConversation(usernames, title);
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const isGroupConversation = selectedUsers.length > 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content new-conversation-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Nova Conversa</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="user-search-section">
            <label>Adicionar participantes:</label>
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Pesquisar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="user-search-input"
              />
              {searchLoading && <div className="search-loading">🔍</div>}
            </div>
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(user => (
                  <div
                    key={user._id.$oid}
                    className="search-result-item"
                    onClick={() => handleUserSelect(user)}
                  >
                    <img
                      src={user.avatar || '/images/default-avatar.png'}
                      alt={user.username}
                      className="user-avatar-small"
                    />
                    <div className="user-info">
                      <span className="username">{user.username}</span>
                      {user.name && <span className="user-name">{user.name}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedUsers.length > 0 && (
            <div className="selected-users-section">
              <label>Participantes selecionados:</label>
              <div className="selected-users">
                {selectedUsers.map(user => (
                  <div key={user._id.$oid} className="selected-user">
                    <img
                      src={user.avatar || '/images/default-avatar.png'}
                      alt={user.username}
                      className="user-avatar-small"
                    />
                    <span>{user.username}</span>
                    <button
                      className="remove-user-button"
                      onClick={() => handleUserRemove(user)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isGroupConversation && (
            <div className="conversation-title-section">
              <label>Título da conversa (opcional):</label>
              <input
                type="text"
                placeholder="Ex: Projeto X, Família, etc."
                value={conversationTitle}
                onChange={(e) => setConversationTitle(e.target.value)}
                className="conversation-title-input"
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="create-button"
            onClick={handleCreateConversation}
            disabled={loading || selectedUsers.length === 0}
          >
            {loading ? 'Criando...' : 'Criar Conversa'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;
